import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  ShieldCheck,
  PiggyBank,
  TrendingUp,
  Building2,
  CreditCard,
  Receipt,
  GraduationCap,
  Briefcase,
  Brain,
  ClipboardList,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Volume2,
} from "lucide-react";
import { services, company } from "../../mock";

const iconMap = {
  ShieldCheck,
  PiggyBank,
  TrendingUp,
  Building2,
  CreditCard,
  Receipt,
  GraduationCap,
  Briefcase,
  Brain,
  ClipboardList,
};

const SCENE_MS = 4800;
const TICK = 40;

export default function ServicesVideo() {
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef(null);

  const total = services.length;

  const goTo = useCallback(
    (i) => {
      setIndex(((i % total) + total) % total);
      setProgress(0);
    },
    [total]
  );

  useEffect(() => {
    if (!playing) return;
    timerRef.current = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          setIndex((i) => (i + 1) % total);
          return 0;
        }
        return p + (TICK / SCENE_MS) * 100;
      });
    }, TICK);
    return () => clearInterval(timerRef.current);
  }, [playing, total]);

  const togglePlay = () => setPlaying((p) => !p);

  const active = services[index];
  const Icon = iconMap[active.icon] || ShieldCheck;
  const num = String(index + 1).padStart(2, "0");

  return (
    <div className="group relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-navy-deep">
      <div className="relative aspect-video">
        {/* Animated cinematic background */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy-light via-navy to-navy-deep" />
        <div
          key={`blob-${index}`}
          className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-gold/20 blur-3xl animate-[fade-up_1.2s_ease-out]"
        />
        <div className="absolute -bottom-24 -left-10 w-80 h-80 rounded-full bg-navy-lighter/40 blur-3xl" />

        {/* Giant ghost number */}
        <div
          key={`num-${index}`}
          className="absolute right-2 sm:right-6 bottom-0 font-serif font-bold text-white/5 leading-none select-none text-[34vw] sm:text-[13rem] animate-[fade-up_0.8s_ease-out]"
        >
          {num}
        </div>

        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 z-10">
          <div className="flex items-center gap-2 text-white/90 text-xs sm:text-sm font-medium">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
            </span>
            {company.shortName} · Services
          </div>
          <div className="text-white/70 text-xs sm:text-sm font-medium tabular-nums">
            {num} / {String(total).padStart(2, "0")}
          </div>
        </div>

        {/* Scene content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-5 z-10">
          <div
            key={`scene-${index}`}
            className="animate-[fade-up_0.7s_ease-out] flex flex-col items-center"
          >
            <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-gold flex items-center justify-center shadow-xl">
              <Icon className="h-8 w-8 sm:h-10 sm:w-10 text-navy" />
            </div>
            <h3 className="mt-4 font-serif text-xl sm:text-3xl font-bold text-white max-w-md leading-tight">
              {active.title}
            </h3>
            <p className="mt-2 text-gray-300 text-xs sm:text-sm max-w-sm leading-relaxed">
              {active.description}
            </p>
          </div>
        </div>

        {/* Big play overlay when paused */}
        {!playing && (
          <button
            onClick={togglePlay}
            aria-label="Play"
            className="absolute inset-0 z-20 flex items-center justify-center bg-navy-deep/30 backdrop-blur-[2px] transition-opacity"
          >
            <span className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-gold flex items-center justify-center shadow-2xl hover:scale-105 transition-transform">
              <Play className="h-7 w-7 sm:h-9 sm:w-9 text-navy ml-1" fill="currentColor" />
            </span>
          </button>
        )}

        {/* Prev / Next */}
        <button
          onClick={() => goTo(index - 1)}
          aria-label="Previous"
          className="absolute left-2 top-1/2 -translate-y-1/2 z-20 h-9 w-9 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          onClick={() => goTo(index + 1)}
          aria-label="Next"
          className="absolute right-2 top-1/2 -translate-y-1/2 z-20 h-9 w-9 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Control bar */}
      <div className="bg-navy-deep px-3 sm:px-4 py-2.5 flex items-center gap-2 sm:gap-3">
        <button
          onClick={togglePlay}
          aria-label={playing ? "Pause" : "Play"}
          className="h-8 w-8 rounded-full bg-gold hover:bg-gold-dark text-navy flex items-center justify-center flex-shrink-0 transition-colors"
        >
          {playing ? (
            <Pause className="h-4 w-4" fill="currentColor" />
          ) : (
            <Play className="h-4 w-4 ml-0.5" fill="currentColor" />
          )}
        </button>

        {/* Segmented progress */}
        <div className="flex-1 flex items-center gap-1">
          {services.map((s, i) => (
            <button
              key={s.id}
              onClick={() => goTo(i)}
              className="flex-1 h-1.5 rounded-full bg-white/15 overflow-hidden"
              aria-label={`Go to ${s.title}`}
            >
              <span
                className="block h-full bg-gold rounded-full"
                style={{
                  width:
                    i < index ? "100%" : i === index ? `${progress}%` : "0%",
                  transition: i === index ? "none" : "width 0.3s ease",
                }}
              />
            </button>
          ))}
        </div>

        <Volume2 className="h-4 w-4 text-white/50 hidden sm:block flex-shrink-0" />
      </div>
    </div>
  );
}
